import React, { useCallback, useEffect, useRef } from "react";
import {
  Box,
  CircularProgress,
  IconButton,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { motion } from "framer-motion";
import { MicIcon, StopIcon } from "../../../assets/icon";
import { useVoiceToText } from "../../../hooks/useVoiceToText";
import { VoiceRecordingPreview } from "../../chat/message-footer/voice-recording-preview/voice-recording-preview";
import {
  fieldWrapperStyle,
  getTextFieldStyle,
  getVoiceButtonStyle,
  hintTextStyle,
  recordingPulseAnimation,
  transcribingProgressStyle,
  validationErrorInsideStyle,
  voiceButtonContainerStyle,
  voicePreviewContainerStyle,
} from "./styles";

interface AssessmentOpenTextFieldProps {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  isAnswerChecked?: boolean;
  points?: number | null;
  focusRequestKey?: number;
  errorMessage?: string | null;
  assessmentId?: string | null;
  questionIndex?: number | null;
}

export const AssessmentOpenTextField: React.FC<AssessmentOpenTextFieldProps> = ({
  value,
  onChange,
  disabled = false,
  isAnswerChecked = false,
  points = null,
  focusRequestKey = 0,
  errorMessage = null,
  assessmentId = null,
  questionIndex = null,
}) => {
  const theme = useTheme();
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const prevFocusRequestKeyRef = useRef(focusRequestKey);
  const valueRef = useRef(value);
  const onChangeRef = useRef(onChange);
  const hasValidationError = Boolean(errorMessage);

  valueRef.current = value;
  onChangeRef.current = onChange;

  // Stable callback + latest value via refs so a late STT result cannot append
  // onto a stale closure, and so useVoiceToText always has a live onChange.
  const handleTranscribed = useCallback((text: string) => {
    const trimmed = valueRef.current.trim();
    onChangeRef.current(trimmed ? `${trimmed} ${text}` : text);
  }, []);

  const {
    isRecording,
    isTranscribing,
    recordedAudioUrl,
    toggleRecording,
    clearRecording,
  } = useVoiceToText(handleTranscribed, {
    assessment_id: assessmentId,
    question_index: questionIndex,
  });

  // Drop preview + invalidate in-flight STT once the answer is locked or the
  // field is disabled, so text cannot leak into the next question.
  useEffect(() => {
    if (!isAnswerChecked && !disabled) {
      return;
    }
    clearRecording();
  }, [isAnswerChecked, disabled, clearRecording]);

  useEffect(() => {
    const previousKey = prevFocusRequestKeyRef.current;
    prevFocusRequestKeyRef.current = focusRequestKey;
    // Only focus when Retry bumps the key — not on mount after Previous/Next.
    if (
      !focusRequestKey ||
      focusRequestKey === previousKey ||
      disabled ||
      isAnswerChecked
    ) {
      return;
    }
    const el = inputRef.current;
    if (!el) {
      return;
    }
    el.focus();
    const len = el.value.length;
    el.setSelectionRange(len, len);
  }, [focusRequestKey, disabled, isAnswerChecked]);

  const voiceDisabled =
    disabled || isTranscribing || Boolean(recordedAudioUrl);
  const showVoiceButton = !isAnswerChecked && !disabled;
  const showRecordingPreview =
    showVoiceButton && Boolean(recordedAudioUrl) && !isTranscribing;

  return (
    <Box>
      <Box sx={fieldWrapperStyle}>
        <TextField
          fullWidth
          multiline
          rows={6}
          value={value}
          inputRef={inputRef}
          error={hasValidationError}
          onChange={(event) => onChange(event.target.value)}
          onPaste={(event) => {
            event.preventDefault();
            return false;
          }}
          disabled={disabled}
          placeholder="Type or say your answer..."
          sx={getTextFieldStyle(theme, {
            showVoiceButton,
            isAnswerChecked,
            hasValue: Boolean(value),
            points,
            hasError: hasValidationError,
          })}
        />
        {hasValidationError ? (
          <Typography sx={validationErrorInsideStyle} role="alert">
            {errorMessage}
          </Typography>
        ) : null}
        {showRecordingPreview ? (
          <Box sx={voicePreviewContainerStyle}>
            <VoiceRecordingPreview
              audioUrl={recordedAudioUrl!}
              onDismiss={clearRecording}
            />
          </Box>
        ) : null}
        {showVoiceButton ? (
          <Box sx={voiceButtonContainerStyle}>
            <Box
              component={isRecording && !isTranscribing ? motion.div : "div"}
              {...(isRecording && !isTranscribing
                ? recordingPulseAnimation
                : {})}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <IconButton
                onClick={toggleRecording}
                disabled={voiceDisabled}
                aria-label={
                  isTranscribing
                    ? "Transcribing speech"
                    : isRecording
                      ? "Stop recording"
                      : "Record answer by voice"
                }
                sx={getVoiceButtonStyle(
                  isRecording && !isTranscribing,
                  isTranscribing,
                )}
              >
                {isTranscribing ? (
                  <CircularProgress size={20} sx={transcribingProgressStyle} />
                ) : isRecording ? (
                  <StopIcon size={20} />
                ) : (
                  <MicIcon />
                )}
              </IconButton>
            </Box>
          </Box>
        ) : null}
      </Box>
      {!isAnswerChecked ? (
        <Typography variant="caption" sx={hintTextStyle(theme)}>
          Type your answer manually or tap the microphone to speak. You can edit
          transcribed text before submitting. Copying and pasting text is not
          allowed.
        </Typography>
      ) : null}
    </Box>
  );
};

export default AssessmentOpenTextField;
